/**
 * collectWebFonts — web-font resolution over a project's font config
 *
 * Pure functions (no React, no DOM) shared by:
 * - the runtime loader (FunnelEngineV2 / editor surfaces)
 * - the published-HTML head bake (builder-service — import this LEAF file
 *   directly, never the module barrel, to avoid dragging .tsx into tsc)
 * - SSR (renderV2)
 *
 * @module font-config/utils
 */

import type {
  PlatformId,
  ProjectFontConfig,
  WebFontAsset,
} from '../types/fontConfig.types';
import {
  getWebFont,
  isWebFontId,
  resolveWebFontForEnv,
} from '../data/googleFontCatalog';
import type { WebFontsEnv } from '../data/googleFontCatalog';

/** Which font slot a funnel uses, and at which explicit weights. */
export interface FunnelFontUsage {
  slot: string;
  weights: number[];
}

export interface CollectWebFontsOptions {
  /**
   * Funnel-scoped filter (publish path): only slots the funnel references
   * survive, and faces are subset to the used weights (+ primaryWeight).
   * Omit for editor/theme previews → all mapped web fonts, all faces.
   */
  usage?: FunnelFontUsage[];
  /** Restrict scanning to these platforms (runtime: the detected one). Omit = all 5. */
  platforms?: PlatformId[];
  /** Bucket env for {env} substitution. Local dev serves from 'dev'. */
  env?: WebFontsEnv;
}

const ALL_PLATFORMS: PlatformId[] = ['ios', 'macos', 'android', 'windows', 'linux'];

const DEFAULT_BODY_WEIGHT = 400;

/**
 * Nearest available weight, CSS font-matching style: smallest absolute
 * distance, ties resolved toward the heavier face.
 */
function nearestWeight(available: number[], requested: number): number {
  let best = available[0];
  for (const weight of available) {
    const better =
      Math.abs(weight - requested) < Math.abs(best - requested) ||
      (Math.abs(weight - requested) === Math.abs(best - requested) && weight > best);
    if (better) best = weight;
  }
  return best;
}

/**
 * Resolve the project's platformMappings into the de-duplicated list of
 * web fonts that must actually be loaded — the source-agnostic contract
 * consumed by ensureWebFontsLoaded and the published-HTML bake.
 *
 * Returns [] when the project maps no web fonts (system-only projects
 * pay zero cost).
 */
export function collectActiveWebFonts(
  config: ProjectFontConfig | null | undefined,
  options: CollectWebFontsOptions = {},
): WebFontAsset[] {
  const mappings = config?.platformMappings;
  if (!mappings) return [];

  const platforms = options.platforms?.length ? options.platforms : ALL_PLATFORMS;
  const usageBySlot = options.usage
    ? new Map(options.usage.map((u) => [u.slot, u.weights]))
    : null;

  const assetById = new Map<string, WebFontAsset>();
  const weightsByAsset = new Map<string, Set<number>>();

  for (const [slot, perPlatform] of Object.entries(mappings)) {
    if (usageBySlot && !usageBySlot.has(slot)) continue;

    for (const platform of platforms) {
      const value = perPlatform?.[platform];
      if (!isWebFontId(value, config)) continue;

      const asset = getWebFont(value, config);
      if (!asset) continue;

      assetById.set(asset.id, asset);
      const weights = weightsByAsset.get(asset.id) ?? new Set<number>();
      const requested = usageBySlot?.get(slot) ?? asset.weights;
      for (const w of requested) weights.add(nearestWeight(asset.weights, w));
      weights.add(asset.primaryWeight);
      weightsByAsset.set(asset.id, weights);
    }
  }

  const env = options.env ?? 'dev';

  return [...assetById.values()].map((asset) => {
    const wanted = weightsByAsset.get(asset.id) ?? new Set(asset.weights);
    const weights = [...wanted].sort((a, b) => a - b);
    return resolveWebFontForEnv(
      {
        ...asset,
        weights,
        faces: asset.faces.filter((face) => wanted.has(face.weight)),
      },
      env,
    );
  });
}

/**
 * Deep-walk a funnel flow document and collect every fontFamily reference
 * (with its sibling fontWeight; absent weight = 400 body default).
 *
 * Intentionally structure-agnostic: any object carrying a string
 * `fontFamily` counts, so new screen types / typography paths are covered
 * without maintaining a path list. Non-slot values are harmless — they
 * simply match nothing in platformMappings.
 */
export function collectFunnelFontUsage(flowDocument: unknown): FunnelFontUsage[] {
  const bySlot = new Map<string, Set<number>>();

  const visit = (node: unknown): void => {
    if (Array.isArray(node)) {
      for (const item of node) visit(item);
      return;
    }
    if (!node || typeof node !== 'object') return;

    const obj = node as Record<string, unknown>;
    if (typeof obj.fontFamily === 'string' && obj.fontFamily) {
      const weights = bySlot.get(obj.fontFamily) ?? new Set<number>();
      weights.add(
        typeof obj.fontWeight === 'number' ? obj.fontWeight : DEFAULT_BODY_WEIGHT,
      );
      bySlot.set(obj.fontFamily, weights);
    }

    for (const value of Object.values(obj)) visit(value);
  };

  visit(flowDocument);

  return [...bySlot.entries()].map(([slot, weights]) => ({
    slot,
    weights: [...weights].sort((a, b) => a - b),
  }));
}
